import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';

import type { ProfileColorMode, RouteFeature, UnitSystem } from '../types';
import {
  buildProfileModel,
  findProfileCursor,
  type ProfileCursor,
  type ProfileTemperature,
  type ProfileViewport,
  type RainRadarSamples
} from './profile-model';

export interface ProfileCoreProps {
  routes: RouteFeature[];
  layout: 'compact' | 'expanded';
  units: UnitSystem;
  colorMode: ProfileColorMode;
  viewport?: ProfileViewport;
  rainSamples: RainRadarSamples;
  labels: {
    profile: string;
    dynamic: string;
    colorMode: string;
    noRain: string;
    max?: string;
    colorModes?: Partial<Record<ProfileColorMode, string>>;
  };
  dynamic?: boolean;
  progressKm?: number;
  currentLocation?: RouteFeature['geometry']['coordinates'][number] | null;
  rainActive?: boolean;
  temperature?: ProfileTemperature;
  onCursor?: (cursor: ProfileCursor | null) => void;
  headerExtra?: ReactNode;
  renderExpandedBefore?: (model: ProfileModel) => ReactNode;
  renderProfileExtra?: (profile: ProfileModel['profiles'][number], profileIndex: number, model: ProfileModel) => ReactNode;
}

export type ProfileModel = NonNullable<ReturnType<typeof buildProfileModel>>;

export function ProfileCore({
  routes,
  layout,
  units,
  colorMode,
  viewport,
  rainSamples,
  labels,
  dynamic = false,
  progressKm,
  currentLocation,
  rainActive = true,
  temperature,
  onCursor,
  headerExtra,
  renderExpandedBefore,
  renderProfileExtra
}: ProfileCoreProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [measuredSize, setMeasuredSize] = useState({ width: 0, height: 0 });
  const [cursor, setCursor] = useState<ProfileCursor | null>(null);
  useEffect(() => () => onCursor?.(null), [onCursor]);
  useEffect(() => {
    const element = panelRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => {
      const rect = element.getBoundingClientRect();
      setMeasuredSize({ width: rect.width, height: rect.height });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);
  const modelViewport = useMemo<ProfileViewport>(() => ({
    width: measuredSize.width || viewport?.width || window.innerWidth || 960,
    height: measuredSize.height || viewport?.height || (layout === 'compact' ? 132 : 190),
    isVisible: viewport?.isVisible
  }), [layout, measuredSize.height, measuredSize.width, viewport]);
  const model = useMemo(() => buildProfileModel(
    routes,
    { layout, colorMode, dynamic, progressKm, currentLocation },
    units,
    modelViewport,
    rainSamples,
    temperature
  ), [colorMode, currentLocation, dynamic, layout, modelViewport, progressKm, rainSamples, routes, temperature, units]);

  if (!model) return null;

  const updateCursor = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const width = rect.width || model.width;
    const height = rect.height || model.height;
    const screenX = ((event.clientX - rect.left) * model.width) / width;
    const screenY = ((event.clientY - rect.top) * model.height) / height;
    if (
      screenX < model.pad.left
      || screenX > model.width - model.pad.right
      || screenY < model.pad.top - 18
      || screenY > model.height - model.pad.bottom + 18
    ) return;
    const next = findProfileCursor(model, screenX, screenY);
    setCursor(next);
    onCursor?.(next);
  };

  const clearCursor = () => {
    setCursor(null);
    onCursor?.(null);
  };

  return (
    <section className={`longitudinal-profile longitudinal-profile--${layout}`} aria-label={labels.profile}>
      <header className="longitudinal-profile__header">
        <strong>{labels.profile}</strong>
        <span>{model.rangeLabel}</span>
        {headerExtra}
      </header>
      <div
        ref={panelRef}
        className="profile-interaction-region"
        data-testid="profile-interaction-region"
        data-sheet-drag-ignore="true"
        onPointerDown={updateCursor}
        onPointerMove={updateCursor}
        onPointerLeave={clearCursor}
      >
        <svg viewBox={`0 0 ${model.width} ${model.height}`} role="img" aria-label={labels.profile}>
          <rect width={model.width} height={model.height} fill="transparent" />
          <defs>
            {model.temperatureOverlays.map((overlay) => (
              <linearGradient key={overlay.gradientId} id={overlay.gradientId} x1={model.pad.left} x2={model.width - model.pad.right} y1="0" y2="0" gradientUnits="userSpaceOnUse">
                {overlay.stops.map((stop, index) => <stop key={index} offset={stop.offset} stopColor={stop.color} />)}
              </linearGradient>
            ))}
          </defs>
          {model.temperatureOverlays.map((overlay) => <path key={overlay.key} d={overlay.skyPath} fill={`url(#${overlay.gradientId})`} opacity="0.72" />)}
          {renderExpandedBefore?.(model)}
          <line className="profile-axis" x1={model.pad.left} x2={model.width - model.pad.right} y1={model.height - model.pad.bottom} y2={model.height - model.pad.bottom} />
          {rainActive ? model.profiles.flatMap((profile) => profile.rainTraces.map((trace) => (
            <line
              key={trace.key}
              className="profile-rain-drop"
              x1={trace.x1}
              x2={trace.x2}
              y1={trace.y1}
              y2={trace.y2}
              stroke="#1d4ed8"
              strokeWidth={trace.width}
              strokeLinecap="round"
              opacity={trace.opacity}
              style={{ animationDelay: `${trace.delayMs}ms` }}
            />
          ))) : null}
          {model.profiles.map((profile, profileIndex) => {
            return (
              <g key={profile.route.properties.route_num}>
                {profile.chunks.map((chunk, index) => (
                  <path key={index} d={chunk.points.map((point, pointIndex) => `${pointIndex === 0 ? 'M' : 'L'}${model.x(point.km).toFixed(1)},${model.y(point.ele).toFixed(1)}`).join('')} fill="none" stroke={chunk.color} strokeWidth={layout === 'compact' ? 2.5 : 3} strokeLinejoin="round" strokeLinecap="round" />
                ))}
                {renderProfileExtra?.(profile, profileIndex, model)}
              </g>
            );
          })}
          {model.locationMarker ? (
            <g>
              <line x1={model.locationMarker.x} y1={model.pad.top} x2={model.locationMarker.x} y2={model.height - model.pad.bottom} stroke="currentColor" strokeWidth="1.4" strokeDasharray="2 3" />
              <circle cx={model.locationMarker.x} cy={model.locationMarker.y} r="5.5" fill="currentColor" stroke="#fff" strokeWidth="2" />
              <text className="profile-label" x={model.locationMarker.x + 8} y={model.locationMarker.y - 8}>{model.locationMarker.label}</text>
            </g>
          ) : null}
          {cursor ? (
            <g>
              <line x1={cursor.pointX} y1={model.pad.top} x2={cursor.pointX} y2={model.height - model.pad.bottom} stroke="currentColor" strokeWidth="1.2" strokeDasharray="4 3" />
              <circle cx={cursor.pointX} cy={cursor.pointY} r="5" fill={cursor.color} stroke="#fff" strokeWidth="2" />
              <text x={Math.min(model.width - 10, cursor.pointX + 8)} y={Math.max(model.pad.top + 10, cursor.pointY - 8)} className="profile-cursor-label">{cursor.label}</text>
            </g>
          ) : null}
        </svg>
      </div>
    </section>
  );
}
